import React, { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { useExportContext } from 'lib/context/ExportContext/ExportContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import useDateTimeFormat from 'lib/hooks/useDateTimeFormat';

import { currentPractice } from 'components/practice/practiceState';
import AGTable from 'components/shared/AGTable/AGTable';
import DisplayButtonAGTable from 'components/shared/AGTable/components/DisplayButtonAGTable/DisplayButtonAGTable';

import { formatInvoiceProcedures } from '../lib/formatInvoiceProcedures';
import { CHILDREN_COLUMN_DEFS, PARENT_COLUMN_DEFS } from '../lib/previevColumnDefs';

const DEFAULT_COL_DEFS = {
  enableRowGroup: true,
  resizable: true,
  filter: false,
  suppressMenu: true,
  suppressMovable: true
};

const GRID_OPTIONS = {
  domLayout: 'autoHeight',
  animateRows: true,
  rowGroupPanelShow: 'always'
};

const NewInvoiceDetailsTable = ({
  invoices,
  exportId,
  isView = false,
  isAEOB = true,
  isDetailed = true
}) => {
  const practice = useRecoilValue(currentPractice);
  const { formatDate } = useDateTimeFormat();
  const { setGridApi } = useExportContext();

  const claims = practice?.display_settings?.claims;

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        columnDefs: CHILDREN_COLUMN_DEFS(formatDate, isAEOB, isDetailed, claims),
        defaultColDef: { flex: 1 },
        suppressHorizontalScroll: true,
        headerHeight: 0,
        onGridReady: (params) => {
          params.api.sizeColumnsToFit();
        },
        getRowStyle: () => ({
          backgroundColor: '#f5f7fa'
        })
      },
      getDetailRowData: (params) => {
        params.successCallback(params.data.children || []);
      }
    };
  }, [isAEOB, isDetailed, claims]);

  const columnDefs = PARENT_COLUMN_DEFS(formatDate, isAEOB, isDetailed, claims);
  const rowData = useMemo(() => formatInvoiceProcedures({ invoices }), [invoices]);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  return (
    <TableContextProvider name="invoice_preview" cols={columnDefs}>
      <div className="flex justify-between">
        <div></div>
        {!isView && <DisplayButtonAGTable />}
      </div>

      <div style={containerStyle} className="invoice-table-container">
        <AGTable
          animateRows={true}
          defaultColDef={DEFAULT_COL_DEFS}
          data={rowData.map((row) => ({ ...row, hasStrike: row?.status === 'cancelled' }))} // with this we also add strikethrough in pdf export
          masterDetail={true}
          onGridReady={(params) => setGridApi(exportId, params.api)}
          groupDefaultExpanded={0}
          detailRowAutoHeight={true}
          detailCellRenderer="agDetailCellRenderer" // We need to check this
          detailCellRendererParams={detailCellRendererParams}
          suppressRowClickSelection={true}
          customClassName="ag-grid-interactive"
          gridOptions={{
            ...GRID_OPTIONS,
            rowGroupPanelShow: !isView,
            isRowMaster: (dataItem) => dataItem.children && dataItem.children.length > 0,
            rowClassRules: {
              'cancelled-row': (params) => params.data?.hasStrike
            }
          }}
        />
      </div>
    </TableContextProvider>
  );
};

export default NewInvoiceDetailsTable;
